import React, { useEffect, useMemo, useState } from 'react';

import { useAppDispatch } from '../../app/hooks';
import { ProductDimensionsPanel } from '../../components/productAnalogs/ProductDimensionsPanel/ProductDimensionsPanel';
import { ProductAnalogsTable } from '../../components/productAnalogs/ProductAnalogsTable/ProductAnalogsTable';
import { ExportCatalogModal } from '../../components/productAnalogs/ExportCatalogModal/ExportCatalogModal';
import { Button } from '../../components/ui/Button/Button';
import { EmptyState } from '../../components/ui/EmptyState/EmptyState';
import {
    FilterBar,
    FilterReset,
    FilterSearch,
    FilterSelect,
    type FilterOption,
} from '../../components/ui/FilterBar/FilterBar';
import { PageHeader } from '../../components/ui/PageHeader/PageHeader';
import { TableFooter } from '../../components/ui/TableFooter/TableFooter';
import { useToast } from '../../components/ui/Toast/ToastProvider';
import { useUpdateQueueItemMutation, useUpdateStandardSizeMutation } from '../../entities/handbook/api/sizeApi';
import {
    productAnalogsApi,
    useGetCatalogEntriesQuery,
    useGetCatalogFiltersQuery,
} from '../../entities/productAnalogs/api/productAnalogsApi';
import type { CatalogEntry, CatalogRole, DimensionValues } from '../../entities/productAnalogs/model/types';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { DEFAULT_PAGE_SIZE } from '../../shared/config/pagination';
import { cx } from '../../utils/cx';
import { formatRangeLabel } from '../../utils/format';
import styles from './ProductAnalogsPage.module.scss';

const ALL = '';

type RoleFilter = CatalogRole | typeof ALL;

const ROLE_OPTIONS: FilterOption<RoleFilter>[] = [
    { value: ALL, label: 'Все типы' },
    { value: 'main', label: 'Основные' },
    { value: 'analog', label: 'Аналоги' },
];

export function ProductAnalogsPage() {
    const notify = useToast();
    const dispatch = useAppDispatch();

    const [query, setQuery] = useState('');
    const [projectId, setProjectId] = useState(ALL);
    const [authorEmail, setAuthorEmail] = useState(ALL);
    const [role, setRole] = useState<RoleFilter>(ALL);
    const [page, setPage] = useState(1);
    const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
    const [selected, setSelected] = useState<CatalogEntry | null>(null)
    const [expanded, setExpanded] = useState<Set<string>>(new Set());
    const [isExportOpen, setExportOpen] = useState(false);

    const debouncedQuery = useDebouncedValue(query.trim(), 500);

    const { data: filters } = useGetCatalogFiltersQuery();

    const { data: entriesPage, isLoading, isFetching } = useGetCatalogEntriesQuery({
        page_num: page,
        page_size: DEFAULT_PAGE_SIZE,
        query: debouncedQuery,
        project_id: projectId,
        author_email: authorEmail,
        role: role || undefined,
    });

    const [updateStandardSize, { isLoading: isSavingStandard }] = useUpdateStandardSizeMutation();
    const [updateQueueItem, { isLoading: isSavingQueue }] = useUpdateQueueItemMutation();

    const entries = entriesPage?.items ?? [];

    const total = entriesPage?.total ?? 0;
    const isFiltered = Boolean(debouncedQuery || projectId || authorEmail || role);
    const listKey = `${page}-${debouncedQuery}-${projectId}-${authorEmail}-${role}`;

    useEffect(() => {
        setSelected(null);
        setExpanded(new Set());
    }, [listKey]);

    const projectOptions = useMemo<FilterOption<string>[]>(
        () => [{ value: ALL, label: 'Все проекты' }, ...(filters?.projects ?? [])],
        [filters],
    );
    const authorOptions = useMemo<FilterOption<string>[]>(
        () => [{ value: ALL, label: 'Все инициаторы' }, ...(filters?.authors ?? [])],
        [filters],
    );

    const withReset = <T,>(set: (value: T) => void) => (value: T) => {
        set(value);
        setPage(1);
    };

    const resetFilters = () => {
        setQuery('');
        setProjectId(ALL);
        setAuthorEmail(ALL);
        setRole(ALL);
        setPage(1);
    };

    const toggleSet = (set: Set<string>, id: string) => {
        const next = new Set(set);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
    };

    const saveDimensions = async (entry: CatalogEntry, values: DimensionValues): Promise<boolean> => {
        const dims = entry.dimensions;
        if (!dims) return false;

        const body = {
            length: values.length,
            width: values.width,
            height: values.height,
            maximum_load_weight: values.weight,
            transport_length: values.transportLength,
            transport_width: values.transportWidth,
            transport_height: values.transportHeight,
            transport_weight: values.transportWeight,
        };

        try {
            await (dims.source === 'reference'
                ? updateStandardSize({ id: dims.id, body })
                : updateQueueItem({ id: dims.id, body })
            ).unwrap();
        } catch {
            return false;
        }

        setSelected({ ...entry, dimensions: { ...dims, ...values } });
        dispatch(productAnalogsApi.util.invalidateTags(['CatalogEntries', 'CatalogEntryAnalogs']));
        notify('Габариты сохранены');
        return true;
    };

    return (
        <div className={styles.page}>
            <PageHeader
                title="Товары и аналоги"
                className={styles.header}
                actions={<Button onClick={() => setExportOpen(true)}>Выгрузить в Excel</Button>}
            />
            <p className={styles.subtitle}>
                Основные товары и найденные для них аналоги. Нажмите на строку, чтобы раскрыть связанные товары.
            </p>

            <FilterBar>
                <FilterSearch
                    value={query}
                    placeholder="Поиск по наименованию товара, модели, артикулу…"
                    onChange={withReset(setQuery)}
                />
                <FilterReset active={isFiltered} onClick={resetFilters} />
                <FilterSelect
                    options={projectOptions}
                    value={projectId}
                    allValue={ALL}
                    onChange={withReset(setProjectId)}
                    searchable
                    searchPlaceholder="Название проекта"
                />
                <FilterSelect
                    options={authorOptions}
                    value={authorEmail}
                    allValue={ALL}
                    onChange={withReset(setAuthorEmail)}
                    searchable
                    searchPlaceholder="ФИО инициатора"
                />
                <FilterSelect options={ROLE_OPTIONS} value={role} allValue={ALL} onChange={withReset(setRole)} align="right" />
            </FilterBar>

            <div className={cx(styles.body, !selected && styles.bodySingle)}>
                <section className={styles.pane} aria-label="Товары и аналоги">
                    <ProductAnalogsTable
                        entries={entries}
                        selectedId={selected?.id ?? null}
                        expanded={expanded}
                        scrollKey={listKey}
                        isLoading={isLoading}
                        loading={isFetching}
                        loadingText="Загружаем товары…"
                        onSelect={setSelected}
                        onToggleExpand={(id) => setExpanded((prev) => toggleSet(prev, id))}
                        footer={
                            total > 0 ? (
                                <TableFooter
                                    page={page}
                                    totalPages={entriesPage?.pages ?? 1}
                                    rangeLabel={formatRangeLabel(page, DEFAULT_PAGE_SIZE, total)}
                                    onPageChange={setPage}
                                />
                            ) : undefined
                        }
                        empty={
                            entries.length === 0 ? (
                                <EmptyState
                                    title="Ничего не нашлось"
                                    description="Проверьте написание или сбросьте фильтры."
                                    action={isFiltered ? { label: 'Сбросить фильтры', onClick: resetFilters } : undefined}
                                />
                            ) : undefined
                        }
                    />
                </section>

                {selected && (
                    <aside className={styles.side}>
                        <ProductDimensionsPanel
                            entry={selected}
                            busy={isSavingStandard || isSavingQueue}
                            onSave={saveDimensions}
                            onClose={() => setSelected(null)}
                        />
                    </aside>
                )}
            </div>

            <ExportCatalogModal open={isExportOpen} onClose={() => setExportOpen(false)} onNotify={notify} />
        </div>
    );
}